import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClearIcon from "@mui/icons-material/Clear";
import { Alert, AlertEvent } from "../types";

type Props = {
  alert: Alert;
  events: AlertEvent[];
  newestFirst?: boolean;
  maxHeight?: number | string;
};

const safeJson = (obj: any) => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
};

const formatTs = (ms?: number) => {
  if (!ms) {return "—";}
  const d = new Date(ms);
  const pad = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const statusChipColor = (status: string):
  | "default"
  | "success"
  | "warning"
  | "error"
  | "info" => {
  const s = (status || "").toLowerCase();
  if (["resolved", "closed", "ok", "succeeded", "success"].some((k) => s.includes(k)))
    {return "success";}
  if (["ack", "acknowledged", "silenced"].some((k) => s.includes(k))) {return "info";}
  if (["warn", "warning", "degraded"].some((k) => s.includes(k))) {return "warning";}
  if (["error", "failed", "critical", "firing", "open"].some((k) => s.includes(k)))
    {return "error";}
  return "default";
};

export const AlertLogViewer = ({
  alert,
  events,
  newestFirst = true,
  maxHeight = 520,
}: Props) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [query, setQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("__ALL__");
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const statuses = React.useMemo(() => {
    const s = new Set<string>();
    for (const e of events) {if (e?.status) {s.add(String(e.status));}}
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const rows = React.useMemo(() => {
    const withTs = events.map((e, idx) => {
      const ts = e.timestamp;
      return { e, idx, ts };
    });

    withTs.sort((a, b) => {
      if (a.ts != null && b.ts != null) {return newestFirst ? b.ts - a.ts : a.ts - b.ts;}
      if (a.ts != null && b.ts == null) {return -1;}
      if (a.ts == null && b.ts != null) {return 1;}
      return a.idx - b.idx;
    });

    const q = query.trim().toLowerCase();
    return withTs.filter(({ e }) => {
      if (statusFilter !== "__ALL__" && String(e.status) !== statusFilter) {return false;}
      if (!q) {return true;}

      const msg = String(e.metadata ?? "");
      const metaStr = safeJson(e.metadata).toLowerCase();
      const statusStr = String(e.status || "").toLowerCase();

      return msg.includes(q) || metaStr.includes(q) || statusStr.includes(q);
    });
  }, [events, newestFirst, query, statusFilter]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
  };

  return (
    <Paper sx={{ padding: 2, width: "100%" }}>
        <Stack spacing={1.25}>
          <Stack spacing={0.5}>
            <Stack
              direction={isSm ? "column" : "row"}
              spacing={1}
              alignItems={isSm ? "flex-start" : "baseline"}
              justifyContent="space-between"
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {alert.name}
              </Typography>
              <Chip
                size="small"
                label={alert.integrationId}
                variant="outlined"
                sx={{ fontFamily: "monospace" }}
              />
            </Stack>

            {alert.description ? (
              <Typography variant="body2" color="text.secondary">
                {alert.description}
              </Typography>
            ) : null}

            <Stack
              direction={isSm ? "column" : "row"}
              spacing={1}
              sx={{ pt: 0.5, flexWrap: "wrap" }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                id: <Box component="span" color="text.primary">{alert.id}</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                dedup:{" "}
                <Box component="span" color="text.primary">{alert.foreignKeyValue}</Box>
              </Typography>
            </Stack>
          </Stack>

          <Divider />

          <Stack
            direction={isSm ? "column" : "row"}
            spacing={1}
            alignItems={isSm ? "stretch" : "center"}
          >
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              label="Search..."
              placeholder="Text status or metadata"
              InputProps={{
                endAdornment: query ? (
                  <Tooltip title="Clear">
                    <IconButton size="small" onClick={() => setQuery("")} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : undefined,
              }}
            />

            <FormControl size="small" sx={{ minWidth: isSm ? "100%" : 220 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(String(e.target.value))}
              >
                <MenuItem value="__ALL__">All</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            <Typography variant="caption" color="text.secondary">
              {rows.length} / {events.length}
            </Typography>
          </Stack>

          <Box
            sx={{
              maxHeight,
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            {rows.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No events found
                </Typography>
              </Box>
            ) : (
              rows.map(({ e, ts }, i) => {
                const summary = e.metadata;
                const json = safeJson(e.metadata);
                const panelId = `${i}-${ts ?? "no-ts"}-${String(e.status)}`;

                return (
                  <Accordion
                    key={panelId}
                    disableGutters
                    elevation={0}
                    expanded={expanded === panelId}
                    onChange={(_, isExpanded) => setExpanded(isExpanded ? panelId : false)}
                    sx={{
                      "&:before": { display: "none" },
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderRadius: 0,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: "100%", minWidth: 0 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", color: "text.secondary", minWidth: 210 }}
                        >
                          {formatTs(ts)}
                        </Typography>

                        <Chip
                          size="small"
                          label={String(e.status)}
                          color={statusChipColor(String(e.status))}
                          variant="outlined"
                          sx={{ flexShrink: 0 }}
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {summary}
                        </Typography>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            alertId: <Box component="span" color="text.primary">{e.alertId}</Box>
                          </Typography>

                          <Box sx={{ flex: 1 }} />

                          <Tooltip title="Copy metadata JSON">
                            <IconButton size="small" onClick={() => handleCopy(json)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1.25,
                            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                            borderRadius: 1,
                            overflow: "auto",
                            fontSize: 12,
                            lineHeight: 1.4,
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          }}
                        >
                          {json}
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        </Stack>
    </Paper>
  );
};
